import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["reminderInput", "meetingForm", "meetingTitle", "meetingTime", "meetingClient", "taskFormTemplate"]
  static values = {
    date: String,
    planningMode: { type: Boolean, default: false }
  }

  connect() {
    this.setupDragAndDrop()
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  get currentDate() {
    return this.dateValue || new Date().toISOString().split("T")[0]
  }

  // --- Drag and Drop ---

  setupDragAndDrop() {
    const tasks = this.element.querySelectorAll("[data-draggable-task]")
    tasks.forEach(task => {
      task.setAttribute("draggable", "true")
      task.addEventListener("dragstart", this.handleDragStart.bind(this))
      task.addEventListener("dragend", this.handleDragEnd.bind(this))
    })

    const dropZones = this.element.querySelectorAll("[data-drop-zone]")
    dropZones.forEach(zone => {
      zone.addEventListener("dragover", this.handleDragOver.bind(this))
      zone.addEventListener("drop", this.handleDrop.bind(this))
      zone.addEventListener("dragleave", this.handleDragLeave.bind(this))
    })
  }

  handleDragStart(event) {
    const taskEl = event.target.closest("[data-draggable-task]")
    if (!taskEl) return
    event.dataTransfer.setData("text/plain", taskEl.dataset.taskId)
    taskEl.classList.add("opacity-50", "scale-95")
  }

  handleDragEnd(event) {
    const taskEl = event.target.closest("[data-draggable-task]")
    if (taskEl) taskEl.classList.remove("opacity-50", "scale-95")
  }

  handleDragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add("ring-2", "ring-[#C4A484]", "ring-inset")
  }

  handleDragLeave(event) {
    event.currentTarget.classList.remove("ring-2", "ring-[#C4A484]", "ring-inset")
  }

  handleDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove("ring-2", "ring-[#C4A484]", "ring-inset")

    const taskId = event.dataTransfer.getData("text/plain")
    const dropZone = event.currentTarget.dataset.dropZone

    if (dropZone === "tomorrow") {
      this.moveToTomorrow(taskId)
    } else {
      // Dropped on a time slot
      this.moveToTimeSlot(taskId, dropZone)
    }
  }

  // --- Time Slot Click to Add ---

  handleSlotClick(event) {
    const button = event.currentTarget
    const timeSlot = button.dataset.timeSlot
    const slotContainer = button.closest("[data-time-slot]").querySelector(".flex-1")

    this.showAddItemForm(slotContainer, timeSlot)
  }

  showAddItemForm(container, timeSlot) {
    // Remove any existing form
    const existingForm = this.element.querySelector("[data-add-item-form]")
    if (existingForm) existingForm.remove()

    if (!this.hasTaskFormTemplateTarget) return

    const template = this.taskFormTemplateTarget
    const form = template.content.cloneNode(true).firstElementChild
    form.dataset.timeSlot = timeSlot

    // Set up the save button with the time slot
    const saveBtn = form.querySelector('[data-action="click->day-view#saveNewItem"]')
    if (saveBtn) saveBtn.dataset.timeSlot = timeSlot

    // Escape key to cancel
    const titleInput = form.querySelector('[data-field="title"]')
    if (titleInput) {
      titleInput.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.cancelAddItem()
        if (e.key === "Enter") {
          e.preventDefault()
          this.saveNewItem({ currentTarget: saveBtn })
        }
      })
    }

    container.appendChild(form)
    titleInput?.focus()
  }

  cancelAddItem() {
    const form = this.element.querySelector("[data-add-item-form]")
    if (form) form.remove()
  }

  async saveNewItem(event) {
    const form = this.element.querySelector("[data-add-item-form]")
    if (!form) return

    const timeSlot = event.currentTarget.dataset.timeSlot || form.dataset.timeSlot
    const title = form.querySelector('[data-field="title"]')?.value?.trim()
    const clientId = form.querySelector('[data-field="client"]')?.value
    const taskType = form.querySelector('[data-field="task_type"]')?.value || "general"

    if (!title) return

    const taskData = {
      title: title,
      scheduled_date: this.currentDate,
      scheduled_time_slot: timeSlot,
      task_type: taskType
    }
    if (clientId) taskData.client_id = clientId

    try {
      const response = await fetch("/admin/tasks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ shared_task: taskData })
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const data = await response.json().catch(() => ({}))
        console.error("Failed to create task:", data.errors)
      }
    } catch (error) {
      console.error("Failed to create task:", error)
    }
  }

  // --- Task Completion ---

  async completeTask(event) {
    event.preventDefault()
    event.stopPropagation()

    const taskId = event.currentTarget.dataset.taskId
    const taskElement = event.currentTarget.closest("[data-task-item]")

    // Optimistic UI: wash to green, strikethrough
    if (taskElement) {
      taskElement.classList.add("bg-[#E8F5E9]")
      const titleSpan = taskElement.querySelector("span.flex-1, span.text-sm")
      if (titleSpan) titleSpan.classList.add("line-through", "text-[#8C8C8C]")

      // Swap checkbox to filled
      const btn = event.currentTarget
      btn.innerHTML = '<svg class="w-5 h-5 text-[#9CAF88]" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    }

    try {
      const response = await fetch(`/admin/tasks/${taskId}/complete`, {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })
      // A refusal must not stay looking done (2026-09-30). An intern task is
      // checked off by the intern on the Intern Tasks sheet: go there.
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        if (data.url) {
          window.location = data.url
          return
        }
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      // Revert on error
      if (taskElement) {
        taskElement.classList.remove("bg-[#E8F5E9]")
        const titleSpan = taskElement.querySelector("span.flex-1, span.text-sm")
        if (titleSpan) titleSpan.classList.remove("line-through", "text-[#8C8C8C]")
      }
      console.error("Failed to complete task:", error)
    }
  }

  // --- Move Tasks ---

  async moveToTomorrow(taskId) {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowDate = tomorrow.toISOString().split("T")[0]

    try {
      await fetch(`/admin/tasks/${taskId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ shared_task: { scheduled_date: tomorrowDate } })
      })
      window.location.reload()
    } catch (error) {
      console.error("Failed to move task:", error)
    }
  }

  async moveToTimeSlot(taskId, timeSlot) {
    try {
      await fetch(`/admin/tasks/${taskId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ shared_task: { scheduled_time_slot: timeSlot, scheduled_date: this.currentDate } })
      })
      window.location.reload()
    } catch (error) {
      console.error("Failed to move task:", error)
    }
  }

  // --- Meetings ---

  toggleMeetingForm() {
    if (this.hasMeetingFormTarget) {
      this.meetingFormTarget.classList.toggle("hidden")
      if (!this.meetingFormTarget.classList.contains("hidden") && this.hasMeetingTitleTarget) {
        this.meetingTitleTarget.focus()
      }
    }
  }

  async saveMeeting() {
    const title = this.hasMeetingTitleTarget ? this.meetingTitleTarget.value.trim() : ""
    const time = this.hasMeetingTimeTarget ? this.meetingTimeTarget.value : "09:00"
    const clientId = this.hasMeetingClientTarget ? this.meetingClientTarget.value : ""

    if (!title) return

    const taskData = {
      title: title,
      task_type: "meeting",
      scheduled_date: this.currentDate,
      scheduled_time_slot: time
    }
    if (clientId) taskData.client_id = clientId

    try {
      const response = await fetch("/admin/tasks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ shared_task: taskData })
      })

      if (response.ok) {
        window.location.reload()
      }
    } catch (error) {
      console.error("Failed to create meeting:", error)
    }
  }

  // --- Reminders ---

  addReminder(event) {
    if (event.key !== "Enter") return

    const input = this.reminderInputTarget
    const title = input.value.trim()
    if (!title) return

    fetch("/admin/tasks", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({
        shared_task: {
          title: title,
          task_type: "reminder",
          scheduled_date: this.currentDate
        }
      })
    })
    .then(response => {
      if (response.ok) {
        input.value = ""
        window.location.reload()
      }
    })
  }

  async deleteReminder(event) {
    const taskId = event.currentTarget.dataset.taskId
    if (!taskId) return

    try {
      await fetch(`/admin/tasks/${taskId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfToken
        }
      })
      // Remove from DOM
      const taskElement = event.currentTarget.closest("[data-task-item]")
      if (taskElement) {
        taskElement.style.transition = "opacity 0.2s"
        taskElement.style.opacity = "0"
        setTimeout(() => taskElement.remove(), 200)
      }
    } catch (error) {
      console.error("Failed to delete reminder:", error)
    }
  }

  // --- Recent Activity ---

  async dismissActivity(event) {
    const notificationId = event.currentTarget.dataset.notificationId
    if (!notificationId) return

    try {
      await fetch(`/admin/notifications/${notificationId}/mark_read`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken
        }
      })
      const el = event.currentTarget.closest("[data-notification-id]")
      if (el) {
        el.style.transition = "opacity 0.2s"
        el.style.opacity = "0"
        setTimeout(() => el.remove(), 200)
      }
    } catch (error) {
      console.error("Failed to dismiss notification:", error)
    }
  }

  async clearAllActivity(event) {
    event.preventDefault()

    try {
      await fetch("/admin/notifications/mark_all_read", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken
        }
      })
      window.location.reload()
    } catch (error) {
      console.error("Failed to clear activity:", error)
    }
  }

  // --- Plan Tomorrow ---

  togglePlanTomorrow() {
    this.planningModeValue = !this.planningModeValue

    const planPanel = this.element.querySelector("[data-plan-tomorrow-panel]")
    if (planPanel) {
      planPanel.classList.toggle("hidden", !this.planningModeValue)
    }

    const button = this.element.querySelector("[data-plan-tomorrow-button]")
    if (button) {
      button.textContent = this.planningModeValue ? "Done Planning" : "Plan Tomorrow"
    }
  }
}
